import { useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Button,
  Center,
  Checkbox,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconCircleCheck, IconKey } from "@tabler/icons-react";
import {
  PayPalButtons,
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import { useTranslation } from "react-i18next";
import { useSession } from "../../auth/SessionContext";
import { PAYPAL_CLIENT_ID } from "../../../config/paypal";
import { findPlan } from "../../../config/plans";
import { getLineTotal, unitPrice, FULL_PRICE_PCS } from "../PayPalCheckout/UseCart";
import { Edition, Duration } from "../../Settings";
import { DEBUG } from "../..";
import { steamLoginUrl } from "../../auth/steamLogin";
import classes from "./Wizardcheckout.module.css";

type Status = "idle" | "saving" | "success" | "error";

// ACC TV Lifetime owners apply their key once for 15% off — lifetime one-time
// purchases only (recurring PayPal plans have a fixed price). Backend validates
// + marks the key redeemed on capture.
const ACC_TV_DISCOUNT = 0.15;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isValidAccTvKey = (k: string): boolean => UUID_RE.test(k.trim());

interface Props {
  edition: Edition;
  duration: Duration;
  pcs: number;
  startsAt?: string; // ISO date for time-limited one-time (not used for subscriptions)
  // When set, this purchase upgrades an existing TRIAL key (same ident) instead of
  // minting a new key — backend converts the trial grant/base to the bought tier.
  upgradeKeyIdent?: string;
  onDone: () => void; // e.g. close wizard + go to account
}

const editionLabel = (e: Edition) => (e === "pro" ? "LMU TV Pro" : "LMU TV Standard");

// ── Subscription (recurring) button ──────────────────────────────────────────
function SubscribeButton({ edition, duration, pcs, disabled, onStatus, planId, startsAt, upgradeKeyIdent }: {
  edition: Edition; duration: Duration; pcs: number; disabled: boolean;
  onStatus: (s: Status) => void; planId: string; startsAt?: string; upgradeKeyIdent?: string;
}) {
  const [{ isPending }] = usePayPalScriptReducer();
  if (isPending) return <Center py="sm"><Loader color="lmuTVColors" /></Center>;

  // Only defer billing if the user picked a strictly-future start date; PayPal
  // rejects a start_time in the past, so "today" (the default) bills immediately.
  //
  // Currently always undefined: the wizard's date picker is disabled
  // (START_DATE_ENABLED in BuyWizard). Deferring the PAYMENT here without deferring
  // the Key API grant in save-subscription.php shortened the paid period instead of
  // moving it — see docs/lmutv-subscription-start-date.md.
  const todayISO = new Date().toISOString().slice(0, 10);
  const futureStart = startsAt && startsAt > todayISO ? `${startsAt}T00:00:00Z` : undefined;

  const persist = async (subscriptionId: string) => {
    const fd = new FormData();
    fd.append("subscription_id", subscriptionId);
    fd.append("app", "lmutv");
    fd.append("edition", edition);
    fd.append("duration", duration);
    fd.append("quantity", String(pcs));
    fd.append("affiliate", localStorage.getItem("ref") ?? "");
    if (upgradeKeyIdent) fd.append("upgrade_key_ident", upgradeKeyIdent);
    try {
      const r = await fetch("/dyn/save-subscription.php", { method: "POST", body: fd });
      return (await r.json()).success === true;
    } catch { return false; }
  };

  return (
    <div style={{ position: "relative" }} className={classes.checkoutContainer}>
      <PayPalButtons
        key={`${planId}-${pcs}`}
        style={{ layout: "vertical", color: "gold", shape: "rect", label: "subscribe" }}
        disabled={disabled}
        createSubscription={(_d, actions) => actions.subscription.create({ plan_id: planId, quantity: String(pcs), ...(futureStart ? { start_time: futureStart } : {}) })}
        onApprove={async (data) => {
          onStatus("saving");
          const ok = data.subscriptionID ? await persist(data.subscriptionID) : false;
          onStatus(ok ? "success" : "error");
        }}
        onError={() => onStatus("error")}
      />
      {disabled && <div style={{ position: "absolute", inset: 0, backdropFilter: "blur(3px)", background: "rgba(0,0,0,0.15)", borderRadius: 4, cursor: "not-allowed", zIndex: 10 }} />}
    </div>
  );
}

// ── Lifetime (one-time capture) button ───────────────────────────────────────
function LifetimeButton({ edition, pcs, disabled, onStatus, accTvKey, applyDiscount, upgradeKeyIdent }: {
  edition: Edition; pcs: number; disabled: boolean; onStatus: (s: Status) => void;
  accTvKey: string; applyDiscount: boolean; upgradeKeyIdent?: string;
}) {
  const [{ isPending }] = usePayPalScriptReducer();
  if (isPending) return <Center py="sm"><Loader color="lmuTVColors" /></Center>;

  const unit = unitPrice(edition, "lifetime");
  const post = async (mode: "create" | "new", extra: Record<string, string>) => {
    const fd = new FormData();
    fd.append("amount", String(pcs));
    fd.append("affiliate", localStorage.getItem("ref") ?? "");
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    const r = await fetch(`/dyn/lmutv/process-order.php?mode=${mode}`, { method: "POST", body: fd });
    return (await r.json()).success === true;
  };

  return (
    <div style={{ position: "relative" }} className={classes.checkoutContainer}>
      <PayPalButtons
        // Re-render the buttons when the discount flips (the live key check resolves ~500ms
        // AFTER first render): PayPal captures createOrder at mount, so without this the order
        // keeps the full-price closure and PayPal charges the undiscounted total.
        forceReRender={[edition, pcs, applyDiscount]}
        style={{ layout: "vertical", color: "black", shape: "rect" }}
        disabled={disabled}
        createOrder={(_d, actions) => {
          const fullQty = Math.min(pcs, FULL_PRICE_PCS);
          const items: any[] = [{
            name: editionLabel(edition), description: `${edition}-lifetime`, category: "DIGITAL_GOODS",
            quantity: fullQty, unit_amount: { value: unit.toFixed(2), currency_code: "EUR" },
          }];
          if (pcs > FULL_PRICE_PCS) items.push({
            name: editionLabel(edition) + " (50% bulk)", description: `${edition}-lifetime`, category: "DIGITAL_GOODS",
            quantity: pcs - FULL_PRICE_PCS, unit_amount: { value: (unit / 2).toFixed(2), currency_code: "EUR" },
          });
          const itemTotal = getLineTotal({ edition, duration: "lifetime", pcs });
          const discount = applyDiscount ? itemTotal * ACC_TV_DISCOUNT : 0;
          const grand = itemTotal - discount;
          const breakdown: any = { item_total: { value: itemTotal.toFixed(2), currency_code: "EUR" } };
          if (discount > 0) breakdown.discount = { value: discount.toFixed(2), currency_code: "EUR" };
          return actions.order.create({
            intent: "CAPTURE",
            purchase_units: [{ amount: { value: grand.toFixed(2), currency_code: "EUR", breakdown }, items }],
          }).then((orderID: string) => post("create", { id: orderID, st: "CREATED" }).then(() => orderID));
        }}
        onApprove={async (_data, actions) => {
          onStatus("saving");
          try {
            const details = await actions.order!.capture();
            const lines = JSON.stringify([{ edition, duration: "lifetime", pcs, startsAt: null }]);
            const extra: Record<string, string> = { em: details.payer?.email_address ?? "", id: details.id!, st: details.status!, lines };
            // Single-use ACC TV Lifetime redemption key — backend validates + marks redeemed.
            if (applyDiscount) extra.accTvKey = accTvKey.trim();
            // Trial upgrade — convert the existing trial key instead of minting a new one.
            if (upgradeKeyIdent) extra.upgrade_key_ident = upgradeKeyIdent;
            const ok = await post("new", extra);
            onStatus(ok ? "success" : "error");
          } catch (e) { if (DEBUG) console.error(e); onStatus("error"); }
        }}
        onError={() => onStatus("error")}
      />
      {disabled && <div style={{ position: "absolute", inset: 0, backdropFilter: "blur(3px)", background: "rgba(0,0,0,0.15)", borderRadius: 4, cursor: "not-allowed", zIndex: 10 }} />}
    </div>
  );
}

export function WizardCheckout({ edition, duration, pcs, startsAt, upgradeKeyIdent, onDone }: Props) {
  const { t } = useTranslation();
  const { sessionData } = useSession();
  const [tos, setTos] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [accTvKey, setAccTvKey] = useState("");
  const [accTvCheck, setAccTvCheck] = useState<"idle" | "checking" | "valid" | "invalid" | "notEligible">("idle");

  const plan = findPlan("lmutv", edition, duration);
  const isSub = plan?.type === "subscription";
  const total = plan ? getLineTotal({ edition, duration, pcs }) : 0;
  const loggedIn = !!sessionData?.logged_in;

  // ACC TV holder discount is one-time (lifetime) only — recurring PayPal plans
  // charge a fixed price, so the key field is hidden for subscriptions.
  const accTvKeyTrim = accTvKey.trim();

  // Live server validation: only a genuine, unredeemed, owner-scoped ACC TV Lifetime key
  // earns the 15% discount. The cart shows it (and PayPal charges it) only after the server
  // confirms — so the displayed price always matches what process-order.php accepts at
  // capture (no more "-15% shown but charged/booked at full price → refunded" mismatch).
  useEffect(() => {
    if (isSub || accTvKeyTrim.length === 0) { setAccTvCheck("idle"); return; }
    if (!isValidAccTvKey(accTvKeyTrim)) { setAccTvCheck("invalid"); return; }
    let cancelled = false;
    setAccTvCheck("checking");
    const timer = setTimeout(async () => {
      try {
        const fd = new FormData();
        fd.append("key", accTvKeyTrim);
        const r = await fetch("/dyn/lmutv/validate-acctv-key.php", { method: "POST", body: fd, credentials: "include" });
        const ok = (await r.json()).valid === true;
        if (!cancelled) setAccTvCheck(ok ? "valid" : "notEligible");
      } catch { if (!cancelled) setAccTvCheck("notEligible"); }
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [accTvKeyTrim, isSub]);

  const accTvValid = !isSub && accTvCheck === "valid";
  const discount = accTvValid ? total * ACC_TV_DISCOUNT : 0;
  const grandTotal = total - discount;

  if (status === "success") {
    return (
      <Alert icon={<IconCircleCheck size={20} />} color="green" radius="md" title={t("subscribe.successTitle")}>
        <Text size="sm" mb="sm">{t("subscribe.successMessage")}</Text>
        <Button color="lmuTVColors" onClick={onDone} fullWidth>{t("menu.user.account")}</Button>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {accTvValid ? (
        // Old price (struck) → −discount → new price, so it never reads as the discount
        // being subtracted a second time from the already-discounted total.
        <Stack gap={4}>
          <Group justify="space-between">
            <Text fw={600}>{editionLabel(edition)} · {t(`cart.durations.${duration}`)} · {pcs} {t("cart.pcs")}</Text>
            <Text size="sm" c="dimmed" td="line-through">{total.toFixed(2)}€</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="lmuTVColors.4">{t("cart.accTvKey.discountLine")}</Text>
            <Text size="sm" c="lmuTVColors.4">−{discount.toFixed(2)}€</Text>
          </Group>
          <Group justify="space-between" align="baseline">
            <Text fw={600}>{t("cart.total")}</Text>
            <Text fw={700} size="lg">{grandTotal.toFixed(2)}€</Text>
          </Group>
        </Stack>
      ) : (
        <Group justify="space-between">
          <Text fw={600}>{editionLabel(edition)} · {t(`cart.durations.${duration}`)} · {pcs} {t("cart.pcs")}</Text>
          <Text fw={700} size="lg">{grandTotal.toFixed(2)}€{isSub ? ` / ${t(`subscribe.interval.${duration}`)}` : ""}</Text>
        </Group>
      )}
      {isSub && pcs > FULL_PRICE_PCS && <Text size="xs" c="lmuTVColors.4">{t("cart.bulkNote")}</Text>}
      <Divider />

      {status === "error" && (
        <Alert color="red" radius="md" withCloseButton onClose={() => setStatus("idle")}>
          {t("subscribe.errorMessage")}
        </Alert>
      )}

      {!loggedIn ? (
        <Button
          component="a"
          href={steamLoginUrl()}
          onClick={() => {
            // Persist the current selection across the Steam round-trip so the wizard
            // reopens on this tier after login (sessionStorage survives the redirect).
            try {
              sessionStorage.setItem("lmutv_buy_intent", JSON.stringify({ edition, duration, pcs }));
            } catch { /* private mode / storage disabled — just skip the convenience */ }
          }}
          color="lmuTVColors"
          fullWidth
        >
          {t("subscribe.loginRequired")}
        </Button>
      ) : (
        <>
          <Checkbox
            checked={tos}
            onChange={() => setTos((v) => !v)}
            label={
              <Text size="xs">
                {t("subscribe.tos.prefix")}{" "}
                <Anchor href="/tos" target="_blank" size="xs">{t("subscribe.tos.tos")}</Anchor>{" "}
                {t("subscribe.tos.and")}{" "}
                <Anchor href="/privacy_web" target="_blank" size="xs">{t("subscribe.tos.privacy")}</Anchor>
              </Text>
            }
          />
          {!plan ? (
            <Alert color="yellow" radius="md">{t("subscribe.notAvailable")}</Alert>
          ) : isSub && !plan.paypalPlanId ? (
            <Alert color="yellow" radius="md">{t("subscribe.notAvailable")}</Alert>
          ) : isSub ? (
            <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, vault: true, intent: "subscription" }}>
              <SubscribeButton edition={edition} duration={duration} pcs={pcs} startsAt={startsAt} upgradeKeyIdent={upgradeKeyIdent} disabled={!tos || status === "saving"} onStatus={setStatus} planId={plan.paypalPlanId as string} />
            </PayPalScriptProvider>
          ) : (
            <>
              {/* ACC TV Lifetime holder discount — one-time purchases only */}
              <TextInput
                label={t("cart.accTvKey.label")}
                description={t("cart.accTvKey.hint")}
                placeholder="00000000-0000-0000-0000-000000000000"
                leftSection={<IconKey size={14} />}
                value={accTvKey}
                onChange={(e) => setAccTvKey(e.currentTarget.value)}
                error={accTvCheck === "invalid" ? t("cart.accTvKey.invalid") : accTvCheck === "notEligible" ? t("cart.accTvKey.notEligible") : undefined}
                rightSection={
                  accTvCheck === "checking"
                    ? <Loader size="xs" color="lmuTVColors" />
                    : accTvValid
                      ? <Text size="xs" c="lmuTVColors.4" pr="sm" fw={600}>−15%</Text>
                      : null
                }
              />
              <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "EUR", intent: "capture", disableFunding: "credit" }}>
                <LifetimeButton edition={edition} pcs={pcs} accTvKey={accTvKey} applyDiscount={accTvValid} upgradeKeyIdent={upgradeKeyIdent} disabled={!tos || status === "saving"} onStatus={setStatus} />
              </PayPalScriptProvider>
            </>
          )}
        </>
      )}
    </Stack>
  );
}
